import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import type { McpEndpointRow, ObserveStreamEvent } from './types';
import {
  exposeLiveFeedEmptyCopy,
  exposeLiveFeedStatusLabel,
  nextExposeLiveFeedPhase,
} from './exposeLiveFeedHonesty';
import { resolveAppName } from './helpers';

type Props = {
  events: ObserveStreamEvent[];
  paused: boolean;
  hasSseUrl: boolean;
  streamOpen: boolean;
  streamError: boolean;
  timedOut: boolean;
  onTogglePause: () => void;
  onRetry: () => void;
  endpoints: McpEndpointRow[];
};

function formatTime(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '--:--:--';
  return d.toLocaleTimeString(undefined, { hour12: false });
}

export default function LiveEventFeed({
  events,
  paused,
  hasSseUrl,
  streamOpen,
  streamError,
  timedOut,
  onTogglePause,
  onRetry,
  endpoints,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const phase = nextExposeLiveFeedPhase({
    paused,
    hasSseUrl,
    eventCount: events.length,
    streamOpen,
    streamError,
    timedOut,
  });
  const copy = exposeLiveFeedEmptyCopy(phase);
  const statusLabel = exposeLiveFeedStatusLabel(phase);

  useEffect(() => {
    if (!autoScroll || paused) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events, autoScroll, paused]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2">Live event feed</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            component="span"
            variant="caption"
            fontWeight={600}
            color={
              phase === 'live'
                ? 'success.main'
                : phase === 'timeout' || phase === 'error'
                  ? 'warning.main'
                  : 'text.secondary'
            }
          >
            {statusLabel}
          </Typography>
          <FormControlLabel
            control={<Switch size="small" checked={autoScroll} onChange={(_, v) => setAutoScroll(v)} />}
            label={<Typography variant="caption">Auto-scroll</Typography>}
          />
          <FormControlLabel
            control={<Switch size="small" checked={!paused} onChange={() => onTogglePause()} />}
            label={<Typography variant="caption">{paused ? 'Paused' : 'Streaming'}</Typography>}
          />
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          height: 256,
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      >
        {phase !== 'live' ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {copy.title}
            </Typography>
            {copy.body ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {copy.body}
              </Typography>
            ) : null}
            {copy.retry ? (
              <Button size="small" onClick={onRetry} sx={{ mt: 1.5 }}>
                Retry
              </Button>
            ) : null}
          </Box>
        ) : (
          events.map((ev) => {
            const isError = ev.success === false;
            return (
              <Box
                key={`${ev.id}-${ev.time}`}
                sx={{
                  display: 'flex',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: isError ? 'error.50' : 'transparent',
                  '&:hover': { bgcolor: isError ? 'error.100' : 'action.hover' },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0 }}>
                  {formatTime(ev.time)}
                </Typography>
                <Typography variant="caption" sx={{ minWidth: 100, flexShrink: 0 }}>
                  {resolveAppName(ev.app_id, endpoints)}
                </Typography>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap title={ev.connection_id}>
                  {ev.label || ev.connection_id}
                </Typography>
                <Typography
                  variant="caption"
                  color={isError ? 'error.main' : 'success.main'}
                  sx={{ width: 48, flexShrink: 0 }}
                >
                  {ev.success === false ? 'ERR' : ev.success === true ? 'OK' : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 56, flexShrink: 0, textAlign: 'right' }}>
                  {ev.latency_ms != null ? `${ev.latency_ms}ms` : 'N/A'}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
